import * as React from "react";
import { useMappedState } from "redux-react-hook";
import _ from "lodash";

import useList from "src/core/lists/hooks/useList";
import { canListOrderProduct } from "src/core/orders/utils/orders";
import { useOrder } from "src/core/orders/hooks";
import useOrderingNotAvailableForDay from "src/core/orders/hooks/validation/useOrderingAvailableForDay";
import { IProduct } from "src/core/products/api/products.api";
import { useDrop } from "src/core/drops/hooks";
import { IStore } from "src/core/shared/store";
import { useProductOrderVariant } from "src/core/orders/hooks/useProductOrderVariant";
import useCallOffs from "src/modules/call-off/hooks/useCallOffs";

export interface ProductIsOrderableResponse {
  orderable: boolean;
  unavailableMessage?: string
}

export default (product: IProduct, unavailable?: string | null, checkPrices?: boolean): ProductIsOrderableResponse => {
  const [ { currentOrder } ] = useOrder();
  const [ { drop } ] = useDrop();
  const orderingNotAvailableForDay = useOrderingNotAvailableForDay(drop);
  const [ { currentList } ] = useList();
  const getProductOrderVariant = useProductOrderVariant();
  const { getProductCallOff } = useCallOffs();

  const {
    orderVariants, terminology, installedModules
  } = useMappedState(({ global }: IStore) => ({
    orderVariants: global.settings?.orderVariants || [],
    terminology: global.settings?.terminology,
    installedModules: global.settings?.installedModules
  }));

  return React.useMemo(() => {
    let canOrderProd: boolean;
    // Default to false
    const productCheckResult: ProductIsOrderableResponse = { orderable: false };
    //is this product in the current order
    const productIsNotOnOrder = !currentOrder?.data?.orderLines.find(ol => ol.productId === product.id);
    //if theres a product with exclusive ordering in the order
    const exclusiveOrder = currentOrder?.data?.orderLines.some(ol => ol.product.exclusiveOnOrder);
    //is this product in the current list
    const productIsNotOnList = !currentList?.data?.listLines.find(ll => ll.productId === product.id);
    //if theres a product with exclusive ordering in the order
    const exclusiveList = currentList?.data?.listLines.some(ll => ll.product.exclusiveOnOrder);

    if (checkPrices) {
      const prices = product.prices;
      const priceCodeId = drop?.priceCodeId;
      const priceExistsForDrop = prices?.find(pri => pri.priceCodeId === priceCodeId);

      if (!priceExistsForDrop) {
        productCheckResult.unavailableMessage = `This product is not available for this ${terminology?.drop.singular.text}`;
      } else {
        productCheckResult.orderable = true;
      }
    }

    if (currentList.data) {
      canOrderProd = canListOrderProduct(product, currentList.data.schedule) === null;
    } else {
      const orderVariant = getProductOrderVariant(product);

      canOrderProd = orderingNotAvailableForDay(
        currentOrder.data && currentOrder.data.orderDeliveryDate ? currentOrder.data.orderDeliveryDate : null,
        orderVariant,
        product
      ) === null;
    }

    //Order order variant product validation
    //if the order is exclusive and this product is not in the order else.
    if (exclusiveOrder && productIsNotOnOrder) {
      productCheckResult.unavailableMessage = "Order contains exclusive product, remove the exclusive product to add others";
      //if theres order lines, this product is an exclusive product and is not on the order.
    } else if (currentOrder?.data?.orderLines.length && productIsNotOnOrder && product.exclusiveOnOrder) {
      productCheckResult.unavailableMessage = "This is an order exclusive product, remove all other products to add this product to the order";
    }

    //if the list is exclusive and this product is not in the list else.
    if (exclusiveList && productIsNotOnList) {
      productCheckResult.unavailableMessage = "List contains exclusive product, remove the exclusive product to add others";
      //if theres order lines, this product is an exclusive product and is not on the order.
    } else if (currentList?.data?.listLines.length && productIsNotOnList && product.exclusiveOnOrder) {
      productCheckResult.unavailableMessage = "This is an order exclusive product, remove all other products to add this product to the order";
    }

    const orderVariantId = currentOrder.data?.orderVariantId ?? currentList.data?.orderVariantId;

    //List order variant product validation
    if (orderVariantId !== product.orderVariantId) {
      const requiredOrderVariant = _.find(orderVariants, ov => ov.id === currentOrder.data?.orderVariantId)?.name;
      const productOrderVariant = _.find(orderVariants, ov => ov.id === product.orderVariantId)?.name;
      const messageContext = currentList.data ? terminology?.list?.singular.text : terminology?.order?.singular.text;
      let message = "This product is not available on the current order variant";

      if (productOrderVariant && requiredOrderVariant) {
        message = `Due to being in the context of a ${requiredOrderVariant} ${messageContext}, the ${productOrderVariant} variant is not avaiable.`;
        message += ` Please create a new ${messageContext} to add this product`;
      }

      if (currentOrder.mode === "edit" || currentList.mode === "edit") {
        // can't order product as its not the same order variation as the order is set up for
        productCheckResult.unavailableMessage = message;
      }
    }

    if (installedModules?.includes("call-off") && product.callOffExclusive && !getProductCallOff(product.id)) {
      productCheckResult.unavailableMessage = `This product is for ${terminology?.callOff?.plural.text ?? "Call offs"} only but there are none assigned to your account.`;
    }

    if (unavailable) {
      return productCheckResult;
    } else if (currentOrder.mode === "edit" && (!canOrderProd || productCheckResult.unavailableMessage)) {
      return productCheckResult;
    } else if (currentList.mode === "edit" && (!canOrderProd || productCheckResult.unavailableMessage)) {
      return productCheckResult;
    } else if (checkPrices) {
      return productCheckResult;
    } else {
      productCheckResult.orderable = true;

      return productCheckResult;
    }
  }, [
    checkPrices,
    currentList.data,
    currentList.mode,
    currentOrder.data,
    currentOrder.mode,
    drop?.priceCodeId,
    getProductCallOff,
    getProductOrderVariant,
    installedModules,
    orderVariants,
    orderingNotAvailableForDay,
    product,
    terminology?.callOff?.plural.text,
    terminology?.drop.singular.text,
    terminology?.list?.singular.text,
    terminology?.order?.singular.text,
    unavailable
  ]);
};
